import type { HeaderUtilityLayer } from "@/components/layout/HeaderUtilitySlot.tsx";
import { createContext, useContext } from "react";

import type { CanvasDisplayMode } from "@/types/agenticUiState.ts";
import type { MessageViewContext } from "@/types/messageViewContext.ts";

/**
 * Outcome of {@link CanvasActionContextValue.push}: `pushed` drilled a layer;
 * `duplicate` (already on top) and `max-depth` are no-ops that keep the stack;
 * `unsupported` = not canvas-renderable.
 */
export type PushResult = "pushed" | "duplicate" | "max-depth" | "unsupported";

/**
 * A surface's stable canvas actions. Actions only: reactive state is managed by the
 * caller, (e.g. `useThreadCanvas`), so this handle's identity never changes and distant
 * consumers (every markdown link) don't re-render when the canvas does.
 */
export type CanvasActionContextValue = {
  /**
   * Reset the canvas modal stack to a single entity URN (an overlay over the
   * resting calendar) and reveal the panel. Returns `false` when the URN isn't
   * canvas-renderable, so the caller can fall back (default navigation, the
   * in-thread lightbox).
   *
   * For a surface that owns one featured slot rather than a trail through it.
   * Two of them:
   *
   * - **Artifacts** — the chip, the omnibox biscuit, a form, and the review
   *   carousel stepping between them. An artifact is what the canvas is *for*,
   *   so opening one is the canvas answering the question it was asked, not a
   *   step deeper into something.
   * - **The calendar** — an event chip, a drag, an event deep link, and the
   *   legacy `?sb_type=eventEdit` URL. The card sits beside a grid that never
   *   goes away, and two events there are siblings rather than one inside the
   *   other.
   *
   * In both, Back would have nothing to mean. {@link push} is for opening
   * something a surface was *referring to*, where there is a trail worth keeping.
   *
   * The agent's own presentation does not come through here at all: a
   * `thread_canvas:update` is merged straight into the query cache by the socket
   * handler, so it replaces the stack without any action being called.
   */
  resetTo: (urn: string) => boolean;
  /**
   * Drill down: push an entity URN onto the modal stack, layering it over the
   * current top (which stays mounted beneath). See {@link PushResult} for the
   * decline reasons a caller must tell apart.
   *
   * For opening something a surface was referring to, where going back to it
   * means something: a contact's own conversation, the routine a thread runs on,
   * an entity a link in the chat names. Artifacts and the calendar deliberately
   * do not — see {@link resetTo}.
   */
  push: (urn: string) => PushResult;
  /** Pop the top modal layer, revealing the one beneath (or the resting calendar). */
  pop: () => void;
  /** Clear the whole modal stack, resting the base calendar. Leaves visibility. */
  clear: () => void;
  /**
   * Remove every layer addressing `urn`, wherever it sits in the stack.
   *
   * For a target the server will never serve — the card reports its own death
   * and the stack drops it, rather than leaving the user on an error card they
   * cannot clear. Positional (`pop`) is wrong here: the dead layer is not
   * necessarily the top one, and popping the top would discard a live card.
   */
  dropContent: (urn: string) => void;
  /** Reveal the canvas panel. */
  open: () => void;
  /** Collapse the canvas panel. */
  close: () => void;
  /**
   * Set the presented entity's sticky display mode (`card` or `full`).
   *
   * Optional, because not every surface gives the card a size to choose: where
   * the card fills a column there is no bounded mode to shrink into, and the
   * card reads the absence as "offer no toggle" rather than being handed an
   * action that does nothing.
   */
  setDisplayMode?: (mode: CanvasDisplayMode) => void;
  /**
   * The on-screen view-context snapshot for the `<ui-context>` tag — the canvas
   * stack top plus the visible calendar window — read live (imperatively) when a
   * message is sent, so the send path doesn't re-render on every canvas change.
   * `undefined` when nothing deictic is on screen (canvas collapsed, or an empty
   * stack with no calendar to report).
   */
  getSnapshot: () => MessageViewContext | undefined;
};

export const CanvasActionContext = createContext<CanvasActionContextValue | undefined>(undefined);

/**
 * What dismissing a card does to the surface around it.
 *
 * `close` puts the card away and leaves what it was covering — the thread
 * screen, where the canvas is a calendar the card sits over.
 *
 * `collapse` takes the column with it, because the column holds nothing else:
 * dismissing the last card empties it, and an empty column has no claim on the
 * width. The control says so, wearing the same panel-collapse glyph the page
 * header uses for the columns either side rather than a dialog's X — the card is
 * not a dialog over the page there, it *is* the column.
 */
export type CanvasDismissKind = "close" | "collapse";

const CanvasDismissContext = createContext<CanvasDismissKind>("close");

export const CanvasDismissProvider = CanvasDismissContext.Provider;

/** How this surface's cards dismiss; `close` wherever no host says otherwise. */
export const useCanvasDismissKind = () => useContext(CanvasDismissContext);

/**
 * Which header-utility layer a card here publishes at, or `null` for a card
 * that must not publish at all.
 *
 * Rendered is not enough to qualify. A card under another keeps its scroll
 * position and its draft, so the stack leaves it mounted and merely
 * `invisible`, and a canvas column that has ever been open stays mounted when
 * it collapses — which is what closing the last card does. Neither is on
 * screen, and either taking the slot would hide the controls in a header
 * nobody can see. Those are the `null` cases.
 *
 * A card that *is* on screen publishes, and the layer says where it sits.
 * `canvas` is the screen's own column. A panel hosting a canvas of its own is
 * not that column, but its cards still cover that panel — header included —
 * so they have to take the slot from the header they are covering rather than
 * leave the controls stranded behind their scrim. Such a host names its own
 * layer, and its cards outrank its header by publishing later at the same one.
 *
 * Providers nest: an inner one narrows to `null`, never widens.
 */
const CanvasHeaderUtilitiesContext = createContext<HeaderUtilityLayer | null>("canvas");

export const CanvasHeaderUtilitiesProvider = CanvasHeaderUtilitiesContext.Provider;

/** The layer a card here publishes at, or `null` if it must not publish. */
export const useCanvasHeaderUtilityLayer = () => useContext(CanvasHeaderUtilitiesContext);

/**
 * The canvas of the surrounding surface, or `undefined` when there's no canvas
 * to drive (mobile, gate-off, or a surface that hasn't wired one). Consumers
 * fall back on `undefined`.
 */
export const useCanvasActionContext = () => useContext(CanvasActionContext);
